/* eslint-disable no-restricted-syntax */

import React from 'react';
import ReactDOMServer from 'react-dom/server';
import { Editor, Transforms, Text } from 'slate';
import { uid } from 'react-uid';

import Leaf from './Leaf';
import Element from './Element';

const LIST_TYPES = ['numbered-list', 'bulleted-list', 'unordered-list', 'ordered-list'];
const BLOCK_FORMATS = [
	'heading-one',
	'heading-two',
	'heading-three',
	'heading-four',
	'block-quote',
	'numbered-list',
	'bulleted-list',
	'ordered-list',
	'unordered-list',
	'indent',
	'outdent'
];

export const HOTKEYS = {
	'mod+b': 'bold',
	'mod+i': 'italic',
	'mod+u': 'underline',
	'mod+`': 'code'
};

export const isBlockActive = (editor, format) => {
	// eslint-disable-next-line no-unused-vars
	for (const [_] of Editor.nodes(editor, {
		match: n => n.type === format
	})) {
		return true;
	}
	return false;
};

export const isMarkActive = (editor, format) => {
	const marks = Editor.marks(editor);
	return marks ? marks[format] === true : false;
};

export const toggleBlock = (editor, format) => {
	const isActive = isBlockActive(editor, format);
	const isList = LIST_TYPES.includes(format);
	Transforms.unwrapNodes(editor, {
		match: n => LIST_TYPES.includes(n.type),
		split: true
	});

	Transforms.setNodes(editor, {
		// eslint-disable-next-line no-nested-ternary
		type: isActive ? 'paragraph' : isList ? 'list-item' : format
	});

	if (!isActive && isList) {
		const block = { type: format, children: [] };
		Transforms.wrapNodes(editor, block);
	}
};

export const toggleMark = (editor, format) => {
	const isActive = isMarkActive(editor, format);

	if (isActive) {
		Editor.removeMark(editor, format);
	} else {
		Editor.addMark(editor, format, true);
	}
};

const mapLeaf = leaf => {
	const marks =
		leaf.marks?.reduce((a, c) => {
			a[c.type] = true;
			return a;
		}, {}) || {};
	return {
		text: leaf.text,
		...marks
	};
};

function migrateNode(node) {
	if (node.object === 'text') {
		return mapLeaf(node);
	}
	if (node.type === 'list-item') {
		return {
			type: node.type,
			children: node.nodes.reduce((a, c) => a.concat(c.nodes.map(migrateNode)), [])
		};
	}
	return {
		type: node.type,
		children: node.nodes.map(migrateNode)
	};
}

export const migrateSlateDataIfNeeded = data => {
	if (data?.document?.nodes) {
		try {
			return data.document.nodes.map(migrateNode);
		} catch (err) {
			console.log(data, err);
		}
	}
	try {
		return data.filter(item => item.children);
	} catch (e) {
		return data;
	}
};

const serializeHtmlNode = node => {
	const { attributes = {}, text, children } = node;
	if (Text.isText(node)) {
		return (
			<Leaf key={uid(node)} leaf={node} attributes={attributes}>
				{text}
			</Leaf>
		);
	}
	const childrenEl = children.map(n => serializeHtmlNode(n));
	return (
		<Element key={uid(node)} element={node} attributes={attributes}>
			{childrenEl}
		</Element>
	);
};

export const htmlSerializer = node => {
	if (!node || !Array.isArray(node)) {
		return '';
	}
	return ReactDOMServer.renderToStaticMarkup(node.map(serializeHtmlNode));
};

export const isBlockFormat = format => BLOCK_FORMATS.includes(format);
